import { AnyAction, Dispatch, Middleware, MiddlewareAPI } from "redux";
import ActionTypes from "../../../constants/ActionTypes";
import { apiRequest } from "../api/actions";
import ApiUrls from "../../../constants/ApiUrls";
import MiddlewareEntities from "../../../constants/MiddlewareEntities";
import { setLoader } from "../ui/actions";
import { setNotification } from "../notification/actions";
import { INotification } from "../notification/types";
import createId from "../../../utilites/createid";
import NotificationTypes from "../../../constants/NotificationTypes";
import { getSessionData } from "../sessionData/actions";
import Config from "../../../config/config";
import { setSSOTestData, setSSOTestToken } from "./actions";
import { SSOTestDataFactory } from "../../../factories/auth/SSOTestDataFactory";
import * as QueryString from "query-string";
import { setShouldCallLogin } from "../cookie/actions";

export const tnAccountLoginMiddleware: Middleware = (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
  next(action);

  const language = store.getState().language.language;
  const labels = require(`../../../translations/${language}`);

  const goToLoginFrom = () => {
    const from = sessionStorage.getItem('loginFrom') ?? '';
    sessionStorage.removeItem('loginFrom');
    window.location.replace(from);
  };

  const errorNotification: INotification[] = [{
    id: parseInt(createId("")),
    type: NotificationTypes.types.ERROR,
    message: labels.errorMessage,
    cssClass: NotificationTypes.cssClass.ALERT_DANGER
  }];

  switch (action.type) {
  case ActionTypes.AUTH.SET_USER_PROFILE_FROM_TN_ACCOUNT:

    const setProfileParams = {
      accessToken: action.payload.accessToken.accessToken,
      refreshToken: action.payload.accessToken.refreshToken,
      expires: action.payload.accessToken.expires,
      tokenType: action.payload.accessToken.tokenType
    };

    sessionStorage.setItem('loginFrom', action.payload.from);

    next(apiRequest(
      null,
      'POST', `${ApiUrls.USER.LOGIN}`,
      null,
      setProfileParams,
      MiddlewareEntities.AUTH.LOGIN,
      {}),
    );
    next(setLoader(true, MiddlewareEntities.AUTH.LOGIN));
    break;
  case `${MiddlewareEntities.AUTH.LOGIN} ${ActionTypes.API_SUCCESS}` :
    next(getSessionData(action.payload.data.session));
    goToLoginFrom();
    break;
  case `${MiddlewareEntities.AUTH.LOGIN} ${ActionTypes.API_ERROR}` :
    next(setNotification(errorNotification, MiddlewareEntities.AUTH.LOGIN));
    next(setLoader(false, MiddlewareEntities.AUTH.LOGIN));
    goToLoginFrom();
    break;
  }
};

export const tnAccountLogoutMiddleware: Middleware = (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
  next(action);

  const goToLogoutFrom = () => {
    window.location.replace(`${Config.tnAccountDomain}/app/logout?post_logout_redirect_uri=${window.location.href}`);
  };

  switch (action.type) {
  case ActionTypes.AUTH.LOGOUT_FROM_TN_ACCOUNT:
    next(apiRequest(
      null,
      'DELETE', `${ApiUrls.USER.LOGOUT}`,
      null,
      null,
      MiddlewareEntities.AUTH.LOGOUT,
      {}),
    );
    next(setLoader(true, MiddlewareEntities.AUTH.LOGOUT));
    break;
  case `${MiddlewareEntities.AUTH.LOGOUT} ${ActionTypes.API_SUCCESS}` :
    next(getSessionData(action.payload.data.session));
    goToLogoutFrom();
    break;
  case `${MiddlewareEntities.AUTH.LOGOUT} ${ActionTypes.API_ERROR}` :
    next(setLoader(false, MiddlewareEntities.AUTH.LOGOUT));
    goToLogoutFrom();
    break;
  case `${MiddlewareEntities.AUTH.LOGOUT_BFF} ${ActionTypes.API_SUCCESS}` :
    next(setLoader(false, MiddlewareEntities.AUTH.LOGOUT_BFF));
    next(setShouldCallLogin(true));
    break;
  }
};

export const SSOMiddleware: Middleware = (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
  next(action);

  const language = store.getState().language.language;
  const labels = require(`../../../translations/${language}`);
  const errorNotification: INotification[] = [{
    id: parseInt(createId("")),
    type: NotificationTypes.types.ERROR,
    message: labels.errorMessage,
    cssClass: NotificationTypes.cssClass.ALERT_DANGER
  }];
  switch (action.type) {
  case ActionTypes.SSO.GET_SSO_REDIRECT:
    const queryString = QueryString.parse(action.payload.substr(1));
    next(apiRequest(
      null,
      'GET', `${ApiUrls.USER.SSO}/`,
      queryString,
      action.payload.data,
      MiddlewareEntities.AUTH.SSO,
      {}),
    );
    break;
  case `${MiddlewareEntities.AUTH.SSO} ${ActionTypes.API_SUCCESS}` :
    window.location.replace(action.payload.data.location);
    break;
  case `${MiddlewareEntities.AUTH.SSO} ${ActionTypes.API_ERROR}` :
    next(setNotification(errorNotification, MiddlewareEntities.AUTH.SSO));
    window.location.replace('/');
    break;
  }
};

export const SSOTestMiddleware: Middleware = (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
  next(action);

  const language = store.getState().language.language;
  const labels = require(`../../../translations/${language}`);
  const errorNotification: INotification[] = [{
    id: parseInt(createId("")),
    type: NotificationTypes.types.ERROR,
    message: labels.errorMessage,
    cssClass: NotificationTypes.cssClass.ALERT_DANGER
  }];
  switch (action.type) {
  case ActionTypes.SSO.FETCH_SSO_TEST_TOKEN:
    next(apiRequest(
      null,
      'POST', `${ApiUrls.USER.SSO}/`,
      null,
      action.payload.data,
      MiddlewareEntities.AUTH.SSO_TEST,
      {}),
    );
    next(setLoader(true, MiddlewareEntities.AUTH.SSO_TEST));
    break;
  case ActionTypes.SSO.FETCH_SSO_TEST_DATA:
    next(apiRequest(
      null,
      'GET', `${ApiUrls.USER.SSO_EXAMPLE}/`,
      null,
      null,
      MiddlewareEntities.AUTH.SSO_TEST_DATA,
      {}),
    );
    break;
  case ActionTypes.SSO.GET_SSO_TEST_REDIRECT:
    const token = store.getState().SSOTest.token;
    const apiUrl = Config.api.baseUrl + ApiUrls.USER.SSO;
    next(apiRequest(
      null,
      'GET', `${apiUrl}?token=${token}`,
      null,
      null,
      MiddlewareEntities.AUTH.SSO_REDIRECT,
      {}),
    );
    break;
  case `${MiddlewareEntities.AUTH.SSO_REDIRECT} ${ActionTypes.API_SUCCESS}` :
    window.open(action.payload.data.location, '_blank');
    break;
  case `${MiddlewareEntities.AUTH.SSO_TEST} ${ActionTypes.API_SUCCESS}` :
    next(setNotification([{
      id: parseInt(createId("")),
      type: NotificationTypes.types.SUCCESS,
      message: `token: ${action.payload.data.token}`,
      cssClass: NotificationTypes.cssClass.ALERT_PRIMARY
    }], MiddlewareEntities.AUTH.SSO_TEST));

    store.dispatch(setSSOTestToken(action.payload.data.token));
    next(setLoader(false, MiddlewareEntities.AUTH.SSO_TEST));
    break;
  case `${MiddlewareEntities.AUTH.SSO_TEST} ${ActionTypes.API_ERROR}` :
    next(setNotification(errorNotification, MiddlewareEntities.AUTH.SSO_TEST));
    next(setLoader(false, MiddlewareEntities.AUTH.SSO_TEST));
    break;
  case `${MiddlewareEntities.AUTH.SSO_TEST_DATA} ${ActionTypes.API_SUCCESS}` :
    store.dispatch(setSSOTestData(SSOTestDataFactory.create(action.payload.data)));
    break;
  case `${MiddlewareEntities.AUTH.SSO_TEST_DATA} ${ActionTypes.API_ERROR}` :
    next(setNotification(errorNotification, MiddlewareEntities.AUTH.SSO_TEST_DATA));
    break;
  }
};

export const authMiddleware = [tnAccountLoginMiddleware, tnAccountLogoutMiddleware, SSOMiddleware, SSOTestMiddleware];
